import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import api from '../../lib/api';
import { auth } from '../../lib/firebase';
import { ApplicationForm } from './applicationForm/types';
import { buildStartPayload, buildSubmitPayload } from './applicationForm/payload';

export * from './applicationForm/types';

export interface ApplicationView {
  id: string;
  status: string;
  legalName: string;
  ownerEmail: string;
  ownerFirstName: string | null;
  emailVerified: boolean;
  isPrepay: boolean;
  submittedAt: string | null;
  customerId: string | null;
  customerStatus: string | null;
  needsInfoMessage: string | null;
}

const qk = {
  myApplication: ['application', 'me'] as const,
};

// Create the Firebase login (no invite gate) and materialize the DRAFT
// application. Surfaces a friendly code for an already-registered email so the
// UI can send them to log in and resume.
export function useCreateApplicantAccount() {
  const qc = useQueryClient();
  return useMutation<ApplicationView, any, ApplicationForm>({
    mutationFn: async (form) => {
      try {
        const cred = await createUserWithEmailAndPassword(
          auth,
          form.ownerEmail.trim(),
          form.password
        );
        const displayName = `${form.ownerFirstName} ${form.ownerLastName}`.trim();
        if (displayName) await updateProfile(cred.user, { displayName });
        await cred.user.getIdToken();
      } catch (err: any) {
        if (err?.code === 'auth/email-already-in-use') {
          const e = new Error('EMAIL_IN_USE');
          (e as any).code = 'EMAIL_IN_USE';
          throw e;
        }
        throw err;
      }
      try {
        const res = await api.post('/applications/start', buildStartPayload(form));
        return res.data.application as ApplicationView;
      } catch (err: any) {
        // The email already belongs to a customer in this org — surface a coded
        // error so the UI can send them to sign in / contact us.
        if (err?.response?.data?.error === 'CUSTOMER_EXISTS') {
          const e = new Error('CUSTOMER_EXISTS');
          (e as any).code = 'CUSTOMER_EXISTS';
          (e as any).customerStatus = err.response.data.customerStatus;
          throw e;
        }
        throw err;
      }
    },
    onSuccess: (app) => qc.setQueryData(qk.myApplication, { application: app }),
  });
}

export function useSaveDraft() {
  return useMutation<ApplicationView, any, ApplicationForm>({
    mutationFn: async (form) => {
      const res = await api.patch('/applications/me', buildStartPayload(form));
      return res.data.application as ApplicationView;
    },
  });
}

export function useSendCode() {
  return useMutation<{ sent?: boolean; alreadyVerified?: boolean }, any, void>({
    mutationFn: async () => (await api.post('/applications/send-code', {})).data,
  });
}

export function useVerifyCode() {
  const qc = useQueryClient();
  return useMutation<{ verified: boolean }, any, string>({
    mutationFn: async (code) => (await api.post('/applications/verify-code', { code })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.myApplication }),
  });
}

// "See attached" upload for the trade-reference sheet.
export function useTradeReferenceUpload() {
  return useMutation<{ fileName: string }, any, File>({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', 'trade_reference');
      return (
        await api.post('/applications/document-upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      ).data;
    },
  });
}

export function useSubmitApplication() {
  const qc = useQueryClient();
  return useMutation<
    { status: string; customerId: string },
    any,
    { form: ApplicationForm; recaptchaToken: string }
  >({
    mutationFn: async ({ form, recaptchaToken }) =>
      (await api.post('/applications/submit', buildSubmitPayload(form, recaptchaToken))).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.myApplication }),
  });
}

export function useApplicationProducts(enabled = true) {
  return useQuery({
    queryKey: ['application', 'products'],
    enabled,
    queryFn: async () =>
      (await api.get('/applications/products')).data as {
        products: { id: string; name: string }[];
      },
  });
}

export function useMyApplication(enabled = true) {
  return useQuery({
    queryKey: qk.myApplication,
    enabled,
    queryFn: async () =>
      (await api.get('/applications/me')).data as { application: ApplicationView | null },
  });
}

// The applicant's full application, for reloading into the wizard to edit and
// resubmit (NEEDS_INFO). Sensitive values come back as on-file flags + last4.
export function useMyApplicationFull(enabled = true) {
  return useQuery({
    queryKey: ['application', 'me', 'full'],
    enabled,
    staleTime: Infinity,
    queryFn: async () => (await api.get('/applications/me/full')).data as { application: any },
  });
}
